import React, { useState, useCallback, useEffect } from 'react'
import {
	Box,
	Heading,
	Text,
	Input,
	Button,
	HStack,
	Wrap,
	WrapItem,
	Badge,
	useToast,
	useColorModeValue,
} from '@chakra-ui/react'

import { parseRankImport } from '../ranking/rankingState'
import { listRankSlots, saveRankSlot, deleteRankSlot, getSlotPayloadObject } from '../ranking/rankingSlotsStorage'

/**
 * @param {{
 *   mode?: 'full' | 'loadOnly'
 *   exportGetSnapshot?: () => object
 *   onLoadSlot: (v2: object) => void
 *   refreshWhenActive?: boolean
 * }} props
 */
export default function RankingSaveSlots({
	mode = 'full',
	exportGetSnapshot,
	onLoadSlot,
	refreshWhenActive,
}) {
	const [slotName, setSlotName] = useState('')
	const [rows, setRows] = useState(() => listRankSlots())
	const toast = useToast()
	const panelBg = useColorModeValue('gray.50', 'bgg.900')
	const chipBg = useColorModeValue('white', 'bgg.900')
	const border = useColorModeValue('gray.200', 'whiteAlpha.300')
	const bodyMuted = useColorModeValue('gray.600', 'bgg.muted')
	const labelMuted = useColorModeValue('gray.600', 'bgg.muted')
	const emptyMuted = useColorModeValue('gray.500', 'gray.400')
	const loadOnly = mode === 'loadOnly'

	const refresh = useCallback(() => {
		setRows(listRankSlots())
	}, [])

	useEffect(() => {
		if (!refreshWhenActive) return
		refresh()
	}, [refreshWhenActive, refresh])

	const handleSave = useCallback(() => {
		if (!exportGetSnapshot) return
		try {
			const data = exportGetSnapshot()
			saveRankSlot(slotName, data)
			setSlotName('')
			refresh()
			toast({ title: 'Saved', description: 'Ranking stored in this browser.', status: 'success', duration: 3000, isClosable: true })
		} catch (e) {
			toast({
				title: 'Could not save',
				description: e instanceof Error ? e.message : 'Unknown error',
				status: 'error',
				duration: 5000,
				isClosable: true,
			})
		}
	}, [exportGetSnapshot, refresh, slotName, toast])

	const handleLoad = useCallback(
		(id) => {
			const raw = getSlotPayloadObject(id)
			if (!raw) {
				toast({ title: 'Slot missing', status: 'warning', duration: 4000, isClosable: true })
				return
			}
			try {
				const v2 = parseRankImport(raw)
				onLoadSlot(v2)
				toast({ title: 'Loaded', description: 'Slot applied — you can keep editing or export again.', status: 'success', duration: 3000, isClosable: true })
			} catch (e) {
				toast({
					title: 'Invalid slot data',
					description: e instanceof Error ? e.message : 'Could not parse JSON',
					status: 'error',
					duration: 6000,
					isClosable: true,
				})
			}
		},
		[onLoadSlot, toast]
	)

	const handleDelete = useCallback(
		(id) => {
			deleteRankSlot(id)
			refresh()
			toast({ title: 'Removed', status: 'info', duration: 2000, isClosable: true })
		},
		[refresh, toast]
	)

	return (
		<Box
			borderWidth={loadOnly ? 0 : '1px'}
			borderColor={border}
			rounded="xl"
			p={loadOnly ? 0 : 5}
			bg={loadOnly ? 'transparent' : panelBg}
		>
			<Heading as="h3" size="sm" mb={2}>
				Saved slots (this browser)
			</Heading>
			<Text fontSize="sm" color={bodyMuted} mb={4} lineHeight="tall">
				{loadOnly ? (
					<>
						Choose a slot to continue editing. Data lives in <strong>localStorage</strong> in this browser only.
					</>
				) : (
					<>
						Store exports in <strong>localStorage</strong> for quick restore. Clearing site data removes them.
					</>
				)}
			</Text>

			{!loadOnly ? (
				<HStack spacing={2} align="flex-end" flexWrap="wrap" mb={4}>
					<Box flex="1" minW="160px">
						<Text fontSize="xs" fontWeight="600" mb={1} color={labelMuted}>
							New slot name
						</Text>
						<Input size="sm" rounded="md" value={slotName} onChange={(e) => setSlotName(e.target.value)} placeholder="e.g. Jan 2026 solo shelf" />
					</Box>
					<Button size="sm" colorScheme="brand" onClick={handleSave} isDisabled={slotName.trim().length === 0 || !exportGetSnapshot}>
						Save current
					</Button>
				</HStack>
			) : null}

			{rows.length === 0 ? (
				<Text fontSize="sm" color={emptyMuted}>
					No slots yet. Save a slot while editing your ranking (step 2) to see it here.
				</Text>
			) : (
				<Wrap spacing={2} shouldWrapChildren>
					{rows.map((row) => (
						<WrapItem key={row.id}>
							<HStack
								spacing={1}
								px={2}
								py={1}
								rounded="md"
								borderWidth="1px"
								borderColor={border}
								bg={chipBg}
							>
								<Badge variant="subtle" fontSize="0.65rem" maxW="140px" isTruncated title={row.name}>
									{row.name}
								</Badge>
								<Button size="xs" variant="ghost" colorScheme="brand" onClick={() => handleLoad(row.id)}>
									Load
								</Button>
								<Button size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(row.id)}>
									Delete
								</Button>
							</HStack>
						</WrapItem>
					))}
				</Wrap>
			)}
		</Box>
	)
}
